/**
 * DataDiscoveryCard Component
 *
 * Interactive card for browsing available data sources.
 * Shows all schemas grouped by category, fully expanded.
 */

import React from 'react';
import {
  ChevronRight,
  Layers,
  BarChart3,
  Clock,
} from 'lucide-react';
import { DataDiscoveryResult } from '../../types/chartDataSource';

interface DataDiscoveryCardProps {
  discoveryResult: DataDiscoveryResult;
  onSelectSchema: (schemaId: string, schemaName: string) => void;
  onExploreCategory: (categoryName: string) => void;
}

export function DataDiscoveryCard({
  discoveryResult,
  onSelectSchema,
  onExploreCategory,
}: DataDiscoveryCardProps) {
  return (
    <div className="p-4 space-y-4">
      {discoveryResult.categories.map((cat) => (
        <div key={cat.name}>
          <div className="flex items-center gap-1.5 mb-2">
            <Layers className="h-3.5 w-3.5 text-emerald-600" />
            <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
              {cat.name} ({cat.schemaCount})
            </span>
          </div>
          <div className="space-y-2">
            {cat.schemas.map((schema) => (
              <button
                key={schema.id}
                onClick={() => onSelectSchema(schema.id, schema.displayName)}
                className="w-full text-left p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900 dark:text-gray-100 group-hover:text-emerald-700 dark:group-hover:text-emerald-400">
                    {schema.displayName}
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500 group-hover:text-emerald-600 dark:group-hover:text-emerald-400" />
                </div>
                {schema.description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                    {schema.description}
                  </p>
                )}
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-400 dark:text-gray-500">
                  <span className="flex items-center gap-1">
                    <BarChart3 className="h-3 w-3" />
                    {schema.numericFieldCount} chartable
                  </span>
                  {schema.hasTimeFields && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Has time data
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default DataDiscoveryCard;
